import { Component } from '@theme/component';

/**
 * Click-to-load video embeds.
 *
 * Nothing is requested from the video host until a visitor presses play, so a product page
 * with a dozen videos costs a dozen poster images rather than a dozen third-party players.
 * That is the difference between a rich page and a slow one.
 *
 * The poster button carries the provider and id; on activation it is replaced in place by an
 * autoplaying iframe. Only the pressed video loads — the rest stay posters.
 *
 * Self-hosted video works the same way and for the same reason: the sources live on the
 * button as data, so a 13 MB file is not fetched by a browser that merely scrolled past it.
 *
 * @extends {Component}
 */
class ProductVideoComponent extends Component {
  /**
   * Replaces a poster with its player. Bound declaratively via `on:click="/loadVideo"`.
   *
   * @param {Event} event
   */
  loadVideo(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('.video-feature__poster');
    if (!(button instanceof HTMLElement)) return;

    const { videoProvider, videoId, videoTitle, videoSources, videoLoop, videoMuted } = button.dataset;
    if (!videoId) return;

    // Self-hosted MP4: a real <video> element, no third-party player at all. Controls are
    // shown and playback starts because the visitor asked for it — never on load, and never
    // muted-autoplay masquerading as an autoplaying background.
    if (videoProvider === 'file') {
      const video = document.createElement('video');

      // Shopify transcodes every upload into several renditions. Offering them all lets the
      // browser take the 480p file on a phone instead of the 1080p master — the difference
      // between a 1.5 Mbps and a 7.2 Mbps fetch, decided by the client rather than by us.
      const urls = videoSources ? videoSources.split(',').filter(Boolean) : [videoId];
      for (const url of urls) {
        const source = document.createElement('source');
        source.src = url;
        source.type = url.includes('.m3u8') ? 'application/x-mpegURL' : 'video/mp4';
        video.appendChild(source);
      }

      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = 'metadata';
      // Ambient editorial footage carries no dialogue, so it loops and stays silent. Muting
      // is also what makes autoplay-on-click reliable rather than blocked by the browser.
      if (videoLoop === 'true') video.loop = true;
      if (videoMuted === 'true') video.muted = true;
      if (videoTitle) video.setAttribute('aria-label', videoTitle);
      button.replaceWith(video);
      video.focus?.();
      return;
    }

    const src = this.#embedUrl(videoProvider, videoId);
    if (!src) return;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = videoTitle || '';
    iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.loading = 'lazy';

    button.replaceWith(iframe);
    // Move focus into the player so keyboard users are not stranded where the button was.
    iframe.focus?.();
  }

  /**
   * @param {string | undefined} provider
   * @param {string} id
   * @returns {string | null}
   */
  #embedUrl(provider, id) {
    // A self-hosted file is not an embed; the caller handles it before reaching here.
    if (provider === 'file') return null;
    // `id` comes from a merchant-controlled metafield, but encode it anyway rather than
    // interpolating raw input into a URL.
    const safeId = encodeURIComponent(id);

    switch (provider) {
      case 'vimeo':
        return `https://player.vimeo.com/video/${safeId}?autoplay=1`;
      case 'youtube':
      default:
        // nocookie host: no YouTube cookie is set unless the visitor actually plays.
        return `https://www.youtube-nocookie.com/embed/${safeId}?autoplay=1&rel=0`;
    }
  }
}

if (!customElements.get('product-video-component')) {
  customElements.define('product-video-component', ProductVideoComponent);
}
